import React, { useState, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaArrowLeft, FaPlus, FaCalendarAlt, FaMapMarkerAlt, FaGraduationCap, FaPlay, FaTimes, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import PageTemplate from '../../components/Common/PageTemplate';

const MezuniyetToreni2025 = () => {
  // 43 fotoğrafın URL'lerini oluştur
  const images = Array.from({ length: 43 }, (_, index) => 
    `https://fs.hacettepe.edu.tr/fenyeni/dosyalar/2025mezuniyettoreni/${index + 1}.JPG`
  );

  const [visibleImages, setVisibleImages] = useState(8);
  const [modalOpen, setModalOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageLoaded, setImageLoaded] = useState({});

  const showMoreImages = () => {
    setVisibleImages(images.length);
  };

  // Modal açma fonksiyonu
  const openModal = useCallback((index) => {
    setCurrentImageIndex(index);
    setModalOpen(true);
    document.body.style.overflow = 'hidden';
  }, []);

  // Modal kapatma fonksiyonu
  const closeModal = useCallback(() => {
    setModalOpen(false);
    document.body.style.overflow = 'unset';
  }, []);

  // Sonraki fotoğraf
  const nextImage = useCallback(() => {
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  }, [images.length]);

  // Önceki fotoğraf
  const prevImage = useCallback(() => {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  }, [images.length]);

  // Klavye kontrolü
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (!modalOpen) return;
      
      switch(e.key) {
        case 'Escape':
          closeModal();
          break;
        case 'ArrowRight':
          nextImage();
          break;
        case 'ArrowLeft':
          prevImage();
          break;
        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [modalOpen, closeModal, nextImage, prevImage]);

  // Fotoğraf yüklenme durumu
  const handleImageLoad = useCallback((index) => {
    setImageLoaded(prev => ({ ...prev, [index]: true }));
  }, []);

  // Preload next and previous images
  useEffect(() => {
    if (modalOpen) {
      const preloadIndexes = [
        (currentImageIndex + 1) % images.length,
        (currentImageIndex - 1 + images.length) % images.length
      ];
      
      preloadIndexes.forEach(index => {
        const img = new Image();
        img.src = images[index];
      });
    }
  }, [currentImageIndex, modalOpen, images]);

  // Modal component
  const ImageModal = () => {
    if (!modalOpen) return null;

    return (
      <div 
        className="fixed inset-0 z-50 bg-black bg-opacity-95 flex items-center justify-center"
        onClick={closeModal}
      >
        {/* Close button */}
        <button
          onClick={closeModal}
          className="absolute top-4 right-4 z-60 text-white hover:text-gray-300 transition-colors"
        >
          <FaTimes size={24} />
        </button>

        {/* Image counter */}
        <div className="absolute top-4 left-4 z-60 text-white bg-black bg-opacity-50 px-3 py-1 rounded-full text-sm">
          {currentImageIndex + 1} / {images.length}
        </div>

        {/* Previous button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            prevImage();
          }}
          className="absolute left-4 z-60 text-white hover:text-gray-300 transition-colors bg-black bg-opacity-50 rounded-full p-2"
        >
          <FaChevronLeft size={20} />
        </button>

        {/* Next button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            nextImage();
          }}
          className="absolute right-4 z-60 text-white hover:text-gray-300 transition-colors bg-black bg-opacity-50 rounded-full p-2"
        >
          <FaChevronRight size={20} />
        </button>

        {/* Main image */}
        <div 
          className="max-w-[90vw] max-h-[90vh] flex items-center justify-center"
          onClick={(e) => e.stopPropagation()}
        >
          <img
            src={images[currentImageIndex]}
            alt={`Mezuniyet Töreni ${currentImageIndex + 1}`}
            className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
            loading="eager"
          />
        </div>

        {/* Image title */}
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-60 text-white bg-black bg-opacity-50 px-4 py-2 rounded-full text-sm">
          Mezuniyet Töreni - Fotoğraf {currentImageIndex + 1}
        </div>
      </div>
    );
  };

  return (
    <PageTemplate
      title="2025 Yılı Mezuniyet Töreni"
      subtitle="Fen Fakültesi mezunları diplomalarını aldı."
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Ana İçerik */}
        <div className="bg-white p-8 md:p-12 rounded-3xl shadow-xl -mt-16 relative z-10 mb-16">
          {/* Etkinlik Bilgileri */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="flex items-center justify-center md:justify-start space-x-3 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl border border-blue-100">
              <FaCalendarAlt className="text-blue-600 text-xl flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-blue-600 uppercase tracking-wide">Tarih</p>
                <p className="text-gray-800 font-semibold">25 Temmuz 2025</p>
                <p className="text-gray-600 text-sm">Cuma, 18:00</p>
              </div>
            </div>
            
            <div className="flex items-center justify-center md:justify-start space-x-3 p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl border border-green-100">
              <FaMapMarkerAlt className="text-green-600 text-xl flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-green-600 uppercase tracking-wide">Mekan</p>
                <p className="text-gray-800 font-semibold">Amfi Tiyatro</p>
                <p className="text-gray-600 text-sm">Hacettepe Üniversitesi</p>
              </div>
            </div>
            
            <div className="flex items-center justify-center md:justify-start space-x-3 p-4 bg-gradient-to-r from-purple-50 to-violet-50 rounded-2xl border border-purple-100">
              <FaGraduationCap className="text-purple-600 text-xl flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-purple-600 uppercase tracking-wide">Akademik Yıl</p>
                <p className="text-gray-800 font-semibold">2024-2025</p>
                <p className="text-gray-600 text-sm">Mezuniyet Töreni</p>
              </div>
            </div>
          </div>

          {/* Ana Metin */}
          <div className="prose prose-lg max-w-none">
            <div className="bg-gradient-to-r from-hacettepe-blue/5 to-hacettepe-red/5 p-8 rounded-2xl border-l-4 border-hacettepe-blue">
              <p className="text-xl leading-relaxed text-gray-700 mb-6">
                <span className="font-semibold text-hacettepe-blue">Hacettepe Üniversitesi Fen Fakültesi</span>'nin 
                <span className="font-semibold"> 2024-2025 Akademik Yılı Mezuniyet Töreni</span>, 
                <span className="font-semibold text-hacettepe-red"> 25 Temmuz 2025 Cuma günü saat 18.00</span>'de, 
                Üniversitemiz <span className="font-semibold">Amfi Tiyatro</span>'sunda gerçekleştirilmiştir.
              </p>
              
              <p className="text-lg leading-relaxed text-gray-700">
                Törende, Fakültemiz programlarından mezuniyet hakkı elde eden öğrencilere diplomaları takdim edilmiştir. 
                Bu özel günde, akademik başarılarının meyvelerini toplayan değerli mezunlarımızı geleceğe uğurladık.
              </p>
            </div>
          </div>
        </div>

        {/* Fotoğraf Galerisi */}
        <div className="mb-16">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-extrabold text-gray-800 mb-4 tracking-tight">
              🎓 Törenden Kareler
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-hacettepe-blue to-hacettepe-red mx-auto rounded-full"></div>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {images.slice(0, visibleImages).map((src, index) => (
              <div 
                key={index} 
                className="group relative aspect-square overflow-hidden rounded-2xl shadow-lg bg-gray-200 cursor-pointer transform hover:scale-105 transition-all duration-300"
                onClick={() => openModal(index)}
              >
                {/* Loading placeholder */}
                {!imageLoaded[index] && (
                  <div className="absolute inset-0 bg-gray-200 animate-pulse flex items-center justify-center">
                    <div className="w-8 h-8 border-2 border-hacettepe-blue border-t-transparent rounded-full animate-spin"></div>
                  </div>
                )}
                
                <img
                  src={src}
                  alt={`Mezuniyet Töreni ${index + 1}`}
                  className={`w-full h-full object-cover transition-all duration-700 ease-out group-hover:scale-110 ${
                    imageLoaded[index] ? 'opacity-100' : 'opacity-0'
                  }`}
                  loading={index < 8 ? "eager" : "lazy"}
                  onLoad={() => handleImageLoad(index)}
                  onError={() => handleImageLoad(index)}
                />
                
                {/* Hover overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-all duration-500"></div>
                
                {/* Hover content */}
                <div className="absolute bottom-4 left-4 right-4 text-white opacity-0 group-hover:opacity-100 transition-all duration-500 transform translate-y-2 group-hover:translate-y-0">
                  <p className="text-sm font-medium">Mezuniyet Töreni</p>
                  <p className="text-xs opacity-75">Fotoğraf {index + 1} • Tıklayın</p>
                </div>

                {/* Click indicator */}
                <div className="absolute top-4 right-4 bg-black bg-opacity-50 rounded-full p-2 opacity-0 group-hover:opacity-100 transition-all duration-300">
                  <div className="w-4 h-4 border-2 border-white rounded-full flex items-center justify-center">
                    <div className="w-2 h-2 bg-white rounded-full"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {visibleImages < images.length && (
            <div className="text-center mt-12">
              <button
                onClick={showMoreImages}
                className="group inline-flex items-center px-8 py-4 bg-gradient-to-r from-hacettepe-blue to-blue-600 text-white font-semibold rounded-full shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-blue-300"
              >
                <FaPlus className="mr-3 -ml-1 h-5 w-5 group-hover:rotate-90 transition-transform duration-300" />
                Tüm Fotoğrafları Gör ({images.length - visibleImages} fotoğraf daha)
              </button>
            </div>
          )}
        </div>

        {/* YouTube Video */}
        <div className="mb-16">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-extrabold text-gray-800 mb-4 tracking-tight">
              🎥 Tören Kayıtları
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-hacettepe-red to-red-500 mx-auto rounded-full"></div>
            <p className="text-gray-600 mt-4 text-lg">Mezuniyet törenimizin canlı yayın kaydını izleyebilirsiniz</p>
          </div>
          
          <div className="relative bg-white p-6 rounded-3xl shadow-xl">
            <div className="relative aspect-video rounded-2xl overflow-hidden bg-gray-100">
              <iframe 
                className="absolute inset-0 w-full h-full"
                src="https://www.youtube.com/embed/9gKJeppW4JY?rel=0&modestbranding=1" 
                title="Fen Fakültesi Mezuniyet Töreni 2025" 
                frameBorder="0" 
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" 
                referrerPolicy="strict-origin-when-cross-origin" 
                allowFullScreen
              />
            </div>
            
            <div className="mt-6 text-center">
              <div className="inline-flex items-center space-x-2 text-gray-600">
                <FaPlay className="text-red-500" />
                <span className="font-medium">Fen Fakültesi Mezuniyet Töreni 2025</span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Geri Dön Butonu */}
        <div className="text-center">
          <Link 
            to="/haberler" 
            className="group inline-flex items-center px-8 py-4 bg-gradient-to-r from-gray-800 to-gray-700 text-white font-semibold rounded-full shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-gray-300"
          >
            <FaArrowLeft className="mr-3 -ml-1 h-5 w-5 group-hover:-translate-x-1 transition-transform duration-300" />
            Tüm Haberlere Geri Dön
          </Link>
        </div>

        {/* Image Modal */}
        <ImageModal />
      </div>
    </PageTemplate>
  );
};

export default MezuniyetToreni2025;